import React, { Component } from 'react';
import { ActionIcon, Loader, Menu, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditOutlined from '@material-ui/icons/EditOutlined';
import MoreVertOutlined from '@material-ui/icons/MoreVertOutlined';
import Star from '@material-ui/icons/Star';
import StarBorderOutlined from '@material-ui/icons/StarBorderOutlined';
import moment from 'moment';
import ChatThreadDeleteConfirmationModal from './ChatThreadDeleteConfirmationModal';

// Content only — deliberately no outer width/height/border here. It's rendered in two
// different containers (the inline expanded panel and the header icon's hover-preview
// popover in ChatAppModal.jsx), each with its own sizing/positioning, so it just fills
// whatever box it's put in instead of assuming one.
export const THREAD_SIDEBAR_WIDTH = 220;

const FALLBACK_THREAD_LABEL = 'Neue Unterhaltung';

export class ChatThreadSidebar extends Component {
  state = {
    pendingDeleteThread: null,
    deletionInProgress: false,
    renamingThreadId: null,
    renameValue: ''
  };

  // Set right before an Escape-triggered state clear, so the blur that follows (Escape
  // doesn't reliably blur a plain text input on its own, but nothing stops a caller from
  // triggering one) is read as a cancel instead of a save — checked synchronously in
  // handleRenameBlur rather than via this.state, which wouldn't yet reflect the clear if
  // both happened in the same event.
  renameCancelled = false;

  formatThreadDate = (thread) => {
    const value = thread?.updatedAt || thread?.createdAt;
    return value ? moment(value).fromNow() : '';
  };

  requestDeleteThread = (thread) => {
    this.setState({ pendingDeleteThread: thread });
  };

  confirmDeleteThread = async () => {
    const { pendingDeleteThread } = this.state;
    const { onDeleteThread } = this.props;
    if (!pendingDeleteThread) return;
    this.setState({ deletionInProgress: true });
    try {
      await onDeleteThread(pendingDeleteThread.id, pendingDeleteThread.folderId);
    } finally {
      this.setState({ deletionInProgress: false, pendingDeleteThread: null });
    }
  };

  startRename = (thread) => {
    this.setState({ renamingThreadId: thread.id, renameValue: thread.title || '' });
  };

  handleRenameKeyDown = (e, threadId) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      this.renameCancelled = true;
      this.setState({ renamingThreadId: null });
    }
  };

  handleRenameBlur = (threadId) => {
    if (this.renameCancelled) {
      this.renameCancelled = false;
      return;
    }
    const value = this.state.renameValue.trim();
    this.setState({ renamingThreadId: null });
    if (value) {
      this.props.onRenameThread(threadId, value);
    }
  };

  renderThreadRow = (thread) => {
    const { activeSessionId, onSelectThread, onPinThread } = this.props;
    const { renamingThreadId, renameValue } = this.state;
    const isActive = thread.id === activeSessionId;

    if (renamingThreadId === thread.id) {
      return (
        <div key={thread.id} style={{ padding: '4px 10px' }}>
          <TextInput
            size={'xs'}
            autoFocus
            value={renameValue}
            onChange={(e) => this.setState({ renameValue: e.currentTarget.value })}
            onKeyDown={(e) => this.handleRenameKeyDown(e, thread.id)}
            onBlur={() => this.handleRenameBlur(thread.id)}
          />
        </div>
      );
    }

    return (
      <div
        key={thread.id}
        onClick={() => onSelectThread(thread.id)}
        className={'ai-chat-thread-row'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          padding: '8px 10px',
          borderRadius: '6px',
          cursor: 'pointer',
          backgroundColor: isActive ? '#E3E3FC' : 'transparent'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          {thread.pinned && <Star style={{ fontSize: '12px', color: '#5555FF', flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <Text size={'sm'} fw={isActive ? 500 : 400} truncate>
              {thread.title || FALLBACK_THREAD_LABEL}
            </Text>
            <Text size={'xs'} c={'dimmed'}>
              {this.formatThreadDate(thread)}
            </Text>
          </div>
        </div>
        <Menu withinPortal position={'bottom-end'} shadow={'md'} zIndex={9999}>
          <Menu.Target>
            <ActionIcon
              size={'sm'}
              style={{ color: '#727293' }}
              variant={'subtle'}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertOutlined style={{ fontSize: '16px' }} />
            </ActionIcon>
          </Menu.Target>
          {/* ChatAppModal's own outside-click handler listens on 'mousedown' (document-level,
              not React's synthetic click delegation) to minimize the chat modal — and this
              dropdown renders through a Mantine portal, outside the modal's DOM subtree, so
              without this it reads every mousedown here as "outside" and minimizes before the
              click (and the item's own action) ever fires. onClick alone doesn't catch this:
              mousedown fires first, as its own separate event. */}
          <Menu.Dropdown onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
            <Menu.Item 
            style={{fontSize: '12px'}}
            leftSection={<EditOutlined style={{ fontSize: '14px', color: '#727293' }} />} 
            onClick={() => this.startRename(thread)}>
              Umbenennen
            </Menu.Item>
            <Menu.Item
              style={{fontSize: '12px'}}
              leftSection={thread.pinned ? <Star style={{ fontSize: '14px', color: '#5555FF' }} /> : <StarBorderOutlined style={{ fontSize: '14px', color: '#727293' }} />}
              onClick={() => onPinThread(thread.id, !thread.pinned)}
            >
              {thread.pinned ? 'Nicht mehr anheften' : 'Anheften'}
            </Menu.Item>
            <Menu.Item
              style={{fontSize: '12px'}}
              leftSection={<DeleteIcon style={{ fontSize: '14px' }} />} 
              color={'red'} 
              onClick={() => this.requestDeleteThread(thread)}>
              Löschen
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    );
  };

  render() {
    const { isLoading, threads, onCreateThread } = this.props;
    const { pendingDeleteThread, deletionInProgress } = this.state;

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', flexShrink: 0 }}>
          <Text size={'sm'} fw={600}>Unterhaltungen</Text>
          <Tooltip label={'Neue Unterhaltung'} withinPortal zIndex={99999}>
            <ActionIcon variant={'subtle'} onClick={() => onCreateThread()}>
              <AddIcon style={{ fontSize: '18px', color: '#5555FF' }} />
            </ActionIcon>
          </Tooltip>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {isLoading ?
            <Stack align={'center'} style={{ padding: '16px 0' }}>
              <Loader size={'sm'} />
            </Stack>
            :
            (threads?.length > 0 ?
              threads.map((thread) => this.renderThreadRow(thread))
              :
              <Text size={'xs'} c={'dimmed'} style={{ padding: '8px 10px' }}>Noch keine Unterhaltungen.</Text>
            )
          }
        </div>
        {pendingDeleteThread &&
        <ChatThreadDeleteConfirmationModal
          open={Boolean(pendingDeleteThread)}
          toggle={() => this.setState({ pendingDeleteThread: null })}
          deletionInProgress={deletionInProgress}
          onConfirm={() => this.confirmDeleteThread()}
        />}
      </>
    );
  }
}

export default ChatThreadSidebar;
